import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { ApiConstants } from 'src/app/core/constants/api-path.constants';
import { ApiService } from 'src/app/core/services/api.service';
import { NotificationService } from 'src/app/core/services/notification.service';

@Component({
  selector: 'app-on-call-booking-approve-dialog',
  templateUrl: './on-call-booking-approve-dialog.component.html',
  styleUrls: ['./on-call-booking-approve-dialog.component.scss']
})
export class OnCallBookingApproveDialogComponent implements OnInit {
  bookingData: any;
  isEditAction = false;
  /** Opened after trip RESET for breakdown — only POST status if allocation changed. */
  isReplacementAfterReset = false;
  _mVehicleTypes: any[] = [];
  mVehicleList: any[] = [];
  mDriverList: any[] = [];
  vehicleTypeId: any = '';
  vendorId: any = '';
  vendorName = '';
  vendorList: { id: any; name: string }[] = [];
  selectedVehicleId: any = '';
  driverId: any = '';
  driverMobileNumber = '';
  vehicleTypeSearch = '';
  vehicleSearch = '';
  driverSearch = '';
  filteredVehicleTypes: any[] = [];
  filteredVehicles: any[] = [];
  filteredDrivers: any[] = [];
  vehicleTypeNoMatch = false;
  vehicleNoMatch = false;
  driverNoMatch = false;
  showVehicleType = (item: any): string => !item || typeof item === 'string' ? (item || '') : (item.vehicleType || '');
  showVehicle = (item: any): string => !item || typeof item === 'string' ? (item || '') : (item.registrationNo || '');
  showDriver = (item: any): string => !item || typeof item === 'string' ? (item || '') : (item.name || '');
  private originalVendorId: any = '';
  private originalVehicleTypeId: any = '';
  private originalVehicleId: any = '';
  private originalDriverId: any = '';

  constructor(
    public dialogRef: MatDialogRef<OnCallBookingApproveDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly apiService: ApiService,
    private notifyService: NotificationService,
    private ngxLoader: NgxUiLoaderService
  ) {
    dialogRef.disableClose = true;
    this.bookingData = data.bookingData;
    this.isEditAction = !!data.isEditAction;
    this.isReplacementAfterReset = !!data.isReplacementAfterReset;
  }

  ngOnInit(): void {
    this.vehicleTypeId = this.bookingData.vehicleType?.id ?? '';
    this.selectedVehicleId = this.bookingData.vehicle?.id ?? '';
    this.driverId = this.bookingData.driver?.id ?? '';
    this.vehicleTypeSearch = this.bookingData.vehicleType?.vehicleType || '';
    this.vehicleSearch = this.bookingData.vehicle?.registrationNo || '';
    this.driverSearch = this.bookingData.driver?.name || '';
    this.captureOriginalAssignment();
    if (!this.isAdminUser) {
      this.resolveDriverMobile();
    }
    this.initVendorThenLoad();
  }

  private captureOriginalAssignment(): void {
    this.originalVendorId = this.bookingData?.vendorMaster?.id ?? '';
    this.originalVehicleTypeId = this.bookingData?.vehicleType?.id ?? '';
    this.originalVehicleId = this.bookingData?.vehicle?.id ?? '';
    this.originalDriverId = this.bookingData?.driver?.id ?? '';
  }

  private hasAssignmentChanged(): boolean {
    return String(this.vendorId ?? '') !== String(this.originalVendorId ?? '')
      || String(this.vehicleTypeId ?? '') !== String(this.originalVehicleTypeId ?? '')
      || String(this.selectedVehicleId ?? '') !== String(this.originalVehicleId ?? '')
      || String(this.driverId ?? '') !== String(this.originalDriverId ?? '');
  }

  /** Default vendor applies only for Admin / Super Admin / Site Admin. */
  get isAdminUser(): boolean {
    return !!this.data?.isAdminUser;
  }

  private get isVendorUser(): boolean {
    return !!this.data?.isVendorUser;
  }

  private initVendorThenLoad(): void {
    // Reset / replacement: admin uses site default vendor; vendor uses login credentials.
    if (this.isReplacementAfterReset) {
      if (this.isAdminUser) {
        this.loadDefaultVendor();
        return;
      }
      this.setVendorFromLoginCredentials();
      if (!this.vendorId) {
        return;
      }
      this.getVehicleTypeByLocationId();
      return;
    }
    if (this.isAdminUser) {
      this.loadDefaultVendor();
      return;
    }
    // Vendor login: always use the logged-in vendor credentials.
    this.setVendorFromLoginCredentials();
    this.getVehicleTypeByLocationId();
  }

  private setVendorFromLoginCredentials(): void {
    const loginVendor = this.data?.loggedInVendor;
    const bookingVendor = this.bookingData?.vendorMaster;
    // Vendor login credentials first; booking vendor as fallback.
    this.vendorId = loginVendor?.id ?? bookingVendor?.id ?? '';
    this.vendorName = loginVendor?.name || bookingVendor?.name || '';
    if (!this.vendorId) {
      this.notifyService.showError('Vendor not found for logged-in user', null);
    }
  }

  onVendorChange(): void {
    const selected = this.vendorList.find(vendor => String(vendor.id) === String(this.vendorId));
    this.vendorName = selected?.name || '';
    this.resetVehicleAndDriver();
    if (this.vehicleTypeId) {
      this.loadVehicles();
    }
  }

  private loadDefaultVendor(): void {
    this.apiService.doGetRequestWithResponse(
      ApiConstants.getAllOnCallDefaultVendorsV2,
      success => {
        this.vendorList = this.extractVendorOptions(success?.body);
        const bookingVendor = this.bookingData?.vendorMaster;
        const matched = this.vendorList.find(vendor => String(vendor.id) === String(bookingVendor?.id));
        if (!matched) {
          this.notifyService.showError('Default vendor not found for this site', null);
          this.getVehicleTypeByLocationId();
          return;
        }
        this.vendorId = String(matched.id);
        this.vendorName = matched.name || bookingVendor?.name || '';
        if (String(this.bookingData?.driver?.vendorMaster?.id
          ?? this.bookingData?.vehicle?.vendorMaster?.id
          ?? '') !== String(this.vendorId)) {
          this.resetVehicleAndDriver();
        }
        this.getVehicleTypeByLocationId();
      },
      error => {
        this.notifyService.showError(error?.message || 'Failed to load default vendor', null);
      }
    );
  }

  private extractVendorOptions(body: any): { id: any; name: string }[] {
    const source = Array.isArray(body)
      ? body
      : Array.isArray(body?.content)
        ? body.content
        : Array.isArray(body?.data)
          ? body.data
          : (body ? [body.vendorMaster || body] : []);
    return source
      .map(entry => {
        const vendor = entry?.vendorMaster || entry;
        return {
          id: vendor?.id ?? vendor?.vendorMasterId ?? vendor?.vendorId,
          name: vendor?.name || vendor?.vendorName || ''
        };
      })
      .filter(vendor => vendor.id != null && vendor.id !== '');
  }

  get dialogTitle(): string {
    if (this.isReplacementAfterReset) {
      return 'Assign Replacement Vehicle';
    }
    return this.isEditAction ? 'Edit On Call Booking' : 'Approve On Call Booking';
  }

  get confirmButtonLabel(): string {
    if (this.isReplacementAfterReset) {
      return 'Assign';
    }
    const status = (this.bookingData?.status || '').toString().toUpperCase();
    if (this.isEditAction && status === 'APPROVED') {
      return 'Change';
    }
    return 'Approve';
  }

  get successMessage(): string {
    if (this.isReplacementAfterReset) {
      return 'Replacement vehicle assigned successfully';
    }
    return this.confirmButtonLabel === 'Change' ? 'Updated successfully' : 'Approved successfully';
  }

  get employeeName(): string {
    const names = this.getPassengerNames();
    return names.length ? names.join(', ') : '';
  }

  private getPassengerNames(): string[] {
    const raw = this.bookingData?.employeeName;
    if (raw == null || raw === '') {
      return [];
    }
    return String(raw)
      .split(',')
      .map(part => part.trim())
      .filter(part => part.length > 0);
  }

  private getDriverContact(driver: any): string {
    const mobile = driver?.contactNumber ?? driver?.contactNo;
    return mobile ? String(mobile).trim() : '';
  }

  private resolveDriverMobile(): void {
    const id = this.driverId;
    if (!id) {
      this.driverMobileNumber = '';
      return;
    }

    const localDriver = this.mDriverList.find(d => d.id == id);
    const mobile = this.getDriverContact(localDriver);
    if (mobile) {
      this.driverMobileNumber = mobile;
      return;
    }

    if (!localDriver && this.mDriverList.length > 0) {
      this.driverMobileNumber = '';
      return;
    }

    this.apiService.doGetRequestWithResponse(
      ApiConstants.getDriverDetailsByIdV3 + id,
      success => {
        if (String(this.driverId) !== String(id)) {
          return;
        }
        if (this.mDriverList.length && !this.mDriverList.some(d => d.id == id)) {
          this.driverMobileNumber = '';
          return;
        }
        const body = success?.body;
        this.driverMobileNumber = this.getDriverContact(Array.isArray(body) ? body[0] : body);
      },
      () => { }
    );
  }

  onDriverChange(): void {
    this.resolveDriverMobile();
  }

  getVehicleTypeByLocationId() {
    const locationId = this.bookingData?.site?.location?.locationId;
    if (!locationId) {
      this.notifyService.showError('Location not found for booking', null);
      return;
    }

    this.apiService.doGetRequestWithResponse(
      ApiConstants.getVehicleTypeByLocationId + '?locationId=' + locationId,
      success => {
        this._mVehicleTypes = success.status == 200 ? (success.body || []) : [];

        const bookingVt = this.bookingData?.vehicleType;
        if (bookingVt?.id && !this._mVehicleTypes.some(vt => vt.id == bookingVt.id)) {
          this._mVehicleTypes = [bookingVt, ...this._mVehicleTypes];
        }

        this.vehicleTypeId = bookingVt?.id || this.vehicleTypeId || '';
        this.syncVehicleTypeLabel();
        if (this.vehicleTypeId) {
          this.loadVehicles();
        } else if (success.status == 204) {
          this.notifyService.showInfo('No Vehicle Type Found', null);
        }
      },
      error => {
        this.notifyService.showError(error.message, null);
      }
    );
  }

  onVehicleTypeChange() {
    this.resetVehicleAndDriver();
    this.loadVehicles();
  }

  onVehicleTypeSearch(value: any): void {
    if (value && typeof value === 'object') {
      this.selectVehicleType(value);
      return;
    }
    const term = String(value ?? '');
    this.vehicleTypeSearch = term;
    this.filteredVehicleTypes = this.optionsForSearch(this._mVehicleTypes, term, item => item?.vehicleType);
    this.vehicleTypeNoMatch = this.isNoMatch(term, this.filteredVehicleTypes);
    const selected = this._mVehicleTypes.find(item => String(item.id) === String(this.vehicleTypeId));
    if (selected && (selected.vehicleType || '') !== term.trim()) {
      this.vehicleTypeId = '';
      this.resetVehicleAndDriver();
    }
  }

  selectVehicleType(vehicleType: any): void {
    if (!vehicleType?.id || String(vehicleType.id) === String(this.vehicleTypeId)) {
      this.vehicleTypeSearch = vehicleType?.vehicleType || this.vehicleTypeSearch;
      return;
    }
    this.vehicleTypeId = vehicleType.id;
    this.vehicleTypeSearch = vehicleType.vehicleType || '';
    this.filteredVehicleTypes = [];
    this.vehicleTypeNoMatch = false;
    this.onVehicleTypeChange();
  }

  onVehicleSearch(value: any): void {
    if (value && typeof value === 'object') {
      this.selectVehicle(value);
      return;
    }
    const term = String(value ?? '');
    this.vehicleSearch = term;
    this.filteredVehicles = this.optionsForSearch(this.mVehicleList, term, item => item?.registrationNo);
    this.vehicleNoMatch = this.isNoMatch(term, this.filteredVehicles);
    const selected = this.mVehicleList.find(item => String(item.id) === String(this.selectedVehicleId));
    if (selected && (selected.registrationNo || '') !== term.trim()) {
      this.selectedVehicleId = '';
      this.driverId = '';
      this.driverSearch = '';
      this.mDriverList = [];
      this.filteredDrivers = [];
      this.driverMobileNumber = '';
    }
  }

  selectVehicle(vehicle: any): void {
    if (!vehicle?.id) {
      return;
    }
    this.vehicleSearch = vehicle.registrationNo || '';
    this.filteredVehicles = [];
    this.vehicleNoMatch = false;
    if (String(vehicle.id) === String(this.selectedVehicleId)) {
      return;
    }
    this.selectedVehicleId = vehicle.id;
    this.onVehicleChange();
  }

  onDriverSearch(value: any): void {
    if (value && typeof value === 'object') {
      this.selectDriver(value);
      return;
    }
    const term = String(value ?? '');
    this.driverSearch = term;
    this.filteredDrivers = this.optionsForSearch(this.mDriverList, term, item => item?.name);
    this.driverNoMatch = this.isNoMatch(term, this.filteredDrivers);
    const selected = this.mDriverList.find(item => String(item.id) === String(this.driverId));
    if (selected && (selected.name || '') !== term.trim()) {
      this.driverId = '';
      this.driverMobileNumber = '';
    }
  }

  selectDriver(driver: any): void {
    if (!driver?.id) {
      return;
    }
    this.driverSearch = driver.name || '';
    this.filteredDrivers = [];
    this.driverNoMatch = false;
    if (String(driver.id) === String(this.driverId)) {
      return;
    }
    this.driverId = driver.id;
    this.onDriverChange();
  }

  showAllVehicleTypes(): void {
    this.filteredVehicleTypes = [...(this._mVehicleTypes || [])];
    this.vehicleTypeNoMatch = false;
  }

  showAllVehicles(): void {
    this.filteredVehicles = [...(this.mVehicleList || [])];
    this.vehicleNoMatch = false;
  }

  showAllDrivers(): void {
    this.filteredDrivers = [...(this.mDriverList || [])];
    this.driverNoMatch = false;
  }

  private isNoMatch(value: string, matches: any[]): boolean {
    return (value || '').trim().length > 1 && matches.length === 0;
  }

  private optionsForSearch(list: any[], value: string, labelOf: (item: any) => string): any[] {
    const source = list || [];
    const term = (value || '').trim().toLowerCase();
    if (term.length <= 1) {
      return [...source];
    }
    return source.filter(item => (labelOf(item) || '').toLowerCase().includes(term));
  }

  private syncVehicleTypeLabel(): void {
    const selected = this._mVehicleTypes.find(item => String(item.id) === String(this.vehicleTypeId));
    if (selected?.vehicleType) {
      this.vehicleTypeSearch = selected.vehicleType;
    }
  }

  private syncVehicleLabel(): void {
    const selected = this.mVehicleList.find(item => String(item.id) === String(this.selectedVehicleId));
    this.vehicleSearch = selected?.registrationNo || '';
  }

  private syncDriverLabel(): void {
    const selected = this.mDriverList.find(item => String(item.id) === String(this.driverId));
    this.driverSearch = selected?.name || '';
  }

  private resetVehicleAndDriver(clearMobile = true): void {
    this.selectedVehicleId = '';
    this.driverId = '';
    this.vehicleSearch = '';
    this.driverSearch = '';
    this.filteredVehicles = [];
    this.filteredDrivers = [];
    this.vehicleNoMatch = false;
    this.driverNoMatch = false;
    this.mVehicleList = [];
    this.mDriverList = [];
    if (clearMobile) {
      this.driverMobileNumber = '';
    }
  }

  loadVehicles() {
    const siteId = this.bookingData?.site?.siteId;
    if (!siteId || !this.vehicleTypeId || !this.vendorId) {
      return;
    }

    const params = `?siteid=${siteId}&vehicletypeid=${this.vehicleTypeId}&vendorid=${this.vendorId}`;
    this.apiService.doGetRequestWithResponse(
      ApiConstants.getVehicleBySiteVendorVehicleType + params,
      success => {
        this.mVehicleList = (success?.body || []).filter(
          vehicle => vehicle.vehicleType?.id == this.vehicleTypeId
        );
        if (!this.mVehicleList.some(v => v.id == this.selectedVehicleId)) {
          this.selectedVehicleId = '';
          this.driverId = '';
          this.vehicleSearch = '';
          this.driverSearch = '';
          this.mDriverList = [];
          this.resolveDriverMobile();
          return;
        }
        this.syncVehicleLabel();
        this.onVehicleChange();
      },
      () => {
        this.resetVehicleAndDriver(false);
        this.resolveDriverMobile();
      }
    );
  }

  onVehicleChange() {
    const vehicle = this.mVehicleList.find(v => v.id == this.selectedVehicleId);
    this.mDriverList = vehicle?.drivers || [];
    if (!this.mDriverList.some(driver => driver.id == this.driverId)) {
      this.driverId = '';
    }
    this.syncDriverLabel();
    this.resolveDriverMobile();
  }

  onApprove() {
    if (!this.vendorId || !this.vehicleTypeId || !this.selectedVehicleId || !this.driverId) {
      this.notifyService.showWarning('Please select vehicle type, vehicle and driver', null);
      return;
    }

    // After trip reset: call API only when allocation changed, and always with RESET.
    if (this.isReplacementAfterReset) {
      if (!this.hasAssignmentChanged()) {
        this.notifyService.showInfo(
          'No assignment change. Trip status remains reset.',
          null
        );
        this.dialogRef.close({ success: false, assignmentChanged: false });
        return;
      }
    }

    const payload: any = {
      specialBookingId: this.bookingData.id,
      bookedFor: this.bookingData.bookedFor?.id,
      action: this.isReplacementAfterReset ? 'RESET' : 'APPROVED',
      vendorId: Number(this.vendorId),
      vehicleId: Number(this.selectedVehicleId),
      vehicleTypeId: Number(this.vehicleTypeId),
      driverId: Number(this.driverId)
    };

    const actionApi = this.isVendorUser
      ? ApiConstants.specialbookingActionByVendor
      : ApiConstants.specialbookingActionByAdmin;

    this.ngxLoader.start();
    this.submitApprove(payload, actionApi);
  }

  private submitApprove(payload: any, actionApi: string): void {
    this.apiService.doPostRequestWithResponse(actionApi, payload,
      success => {
        this.ngxLoader.stop();
        if (success.body?.message === 'Success' || success.body?.success) {
          const vehicle = this.mVehicleList.find(v => v.id == this.selectedVehicleId);
          const driver = this.mDriverList.find(d => d.id == this.driverId)
            || vehicle?.drivers?.find(d => d.id == this.driverId);
          const vehicleType = this._mVehicleTypes.find(vt => vt.id == this.vehicleTypeId);
          this.dialogRef.close({
            success: true,
            assignmentChanged: true,
            apiBody: success.body,
            vendorId: this.vendorId,
            vendorName: this.vendorName,
            vehicleId: this.selectedVehicleId,
            vehicleNumber: vehicle?.registrationNo || '',
            vehicleTypeId: this.vehicleTypeId,
            vehicleType: vehicleType?.vehicleType || vehicle?.vehicleType?.vehicleType || '',
            driverId: this.driverId,
            driverName: driver?.name || ''
          });
          this.notifyService.showSuccess(success.body?.message || this.successMessage, null);
        } else {
          this.notifyService.showWarning(success.body?.message || 'Approve failed', null);
        }
      },
      error => {
        this.ngxLoader.stop();
       console.log('Approve failed', error);
      }
    );
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
